import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BillingStatementReturn } from '../clients/rust-api-client';
import { memberBillingService } from '../clients/ApiClients';
import AppSidebar from '../AppSidebar';
import { Navbar } from '../Components/Catalyst/navbar';
import { SidebarLayout } from '../Components/Catalyst/sidebar-layout';
import { Heading, Subheading } from '../Components/Catalyst/heading';
import { Button } from '../Components/Catalyst/button';
import { Text } from '../Components/Catalyst/text';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import { ErrorPage } from '../Components/Custom/ErrorPage';
import LoadingSpinner from '../LoadingSpinner';
import { useMember } from '../Hooks/MemberContext';
import { apiErrorStatus } from '../utils/apiErrors';
import {
  billingErrorMessage,
  compareMoney,
  formatBillingDate,
  formatMoney,
  savePdfResponse,
  signedAmountParts,
} from '../utils/billingHelpers';
import { entryLabel } from './entryLabels';
import { parsePeriod } from './statementPeriods';

/**
 * One month's statement.
 *
 * **Three balances, and all of them are true.** Opening and closing
 * belong to the period; today's balance belongs to now. On a past
 * month they differ, and the screen must not quietly pick one — the
 * closing balance is what the statement says and today's is what the
 * homeowner owes. front-porch's copy for this is genuinely good and is
 * reused close to verbatim.
 */
const StatementDetailPage: React.FC = () => {
  const { period: periodId } = useParams<{ period: string }>();
  const { selectedHome, loading: memberLoading } = useMember();
  const homeId = selectedHome?.home_id;
  const period = parsePeriod(periodId);

  const [statement, setStatement] = useState<BillingStatementReturn | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  // A failed load leaves nothing to show, so it gets the whole page; a
  // failed download sits beside a statement that did render.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!homeId || !period) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setLoadError(null);
      const { data } = await memberBillingService.getMyStatement({
        homeId,
        from: period.from,
        to: period.to,
      });
      setStatement(data);
    } catch (err) {
      // Never leave another month's statement under this month's title.
      setStatement(null);
      // A home that is not the member's answers 404, never 403.
      setLoadError(
        apiErrorStatus(err) === 404
          ? "We couldn't find that statement."
          : billingErrorMessage(err, "Couldn't load this statement.")
      );
    } finally {
      setLoading(false);
    }
    // `period` is derived from the route param, so keying on its id
    // avoids a new object identity re-running this every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeId, period?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const download = async (): Promise<void> => {
    if (!homeId || !period) return;
    try {
      setDownloading(true);
      const response = await memberBillingService.myStatementPdf(
        { homeId, from: period.from, to: period.to },
        { responseType: 'blob' as const }
      );
      savePdfResponse(response, `statement-${period.id}.pdf`);
    } catch (err) {
      setError(billingErrorMessage(err, "Couldn't download this statement."));
    } finally {
      setDownloading(false);
    }
  };

  if (memberLoading) return <LoadingSpinner />;

  const backdated =
    statement != null &&
    compareMoney(statement.closing_balance, statement.current_balance) !== 0;

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="account" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <Link
        to="/account/statements"
        className="text-sm text-sky-700 hover:underline dark:text-sky-400"
      >
        &larr; All statements
      </Link>

      {!period ? (
        <ErrorPage
          title="Statement not found"
          message="That is not a statement period we can show."
        />
      ) : loadError && !statement ? (
        <ErrorPage
          title="Couldn't load this statement"
          message={loadError}
          onRetry={() => void load()}
        />
      ) : (
        <>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <Heading>{period.label}</Heading>
            <Button
              color="sky"
              onClick={download}
              disabled={downloading || !statement}
            >
              {downloading ? 'Preparing…' : 'Download PDF'}
            </Button>
          </div>

          <ErrorAlert className="mt-4" onDismiss={() => setError(null)}>
            {error}
          </ErrorAlert>

          {loading ? (
            <LoadingSpinner />
          ) : statement ? (
            <>
              <Subheading className="mt-4">{statement.hoa_name}</Subheading>

              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-gray-800">
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">
                    Balance on {formatBillingDate(statement.from)}
                  </div>
                  <div className="mt-1 text-lg tabular-nums text-zinc-900 dark:text-white">
                    {formatMoney(statement.opening_balance)}
                  </div>
                </div>
                <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-gray-800">
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">
                    Balance on {formatBillingDate(statement.to)}
                  </div>
                  <div className="mt-1 text-lg tabular-nums text-zinc-900 dark:text-white">
                    {formatMoney(statement.closing_balance)}
                  </div>
                </div>
                <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-gray-800">
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">
                    Balance today
                  </div>
                  <div className="mt-1 text-lg font-semibold tabular-nums text-zinc-900 dark:text-white">
                    {formatMoney(statement.current_balance)}
                  </div>
                </div>
              </div>

              {backdated && (
                <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50 p-4 dark:border-sky-900 dark:bg-sky-950/40">
                  <Text>
                    <strong>This is a past statement.</strong> It closes at{' '}
                    {formatMoney(statement.closing_balance)} on{' '}
                    {formatBillingDate(statement.to)}, and your account has
                    moved since — it stands at{' '}
                    {formatMoney(statement.current_balance)} today. Both figures
                    are correct; they answer different questions.
                  </Text>
                </div>
              )}

              {statement.lines.length === 0 ? (
                <div className="mt-5 rounded-xl border border-dashed border-zinc-300 p-6 dark:border-zinc-700">
                  <Text>
                    Nothing was posted in {period.label}. The opening and
                    closing balances match because nothing happened, not because
                    your account is empty.
                  </Text>
                </div>
              ) : (
                <ul className="mt-5 divide-y divide-zinc-200 overflow-hidden rounded-xl border border-zinc-200 bg-white dark:divide-zinc-700 dark:border-zinc-700 dark:bg-gray-800">
                  {statement.lines.map((line) => {
                    const parts = signedAmountParts(line.amount);
                    return (
                      <li
                        key={line.id}
                        className="flex items-start justify-between gap-3 px-4 py-3.5"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-zinc-900 dark:text-white">
                            {entryLabel(line.entry_type)}
                          </div>
                          <div className="mt-0.5 truncate text-xs text-zinc-500 dark:text-zinc-400">
                            {formatBillingDate(line.effective_date)}
                            {line.description ? ` · ${line.description}` : ''}
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div
                            className={`text-sm font-semibold tabular-nums ${
                              parts.isCredit
                                ? 'text-lime-700 dark:text-lime-400'
                                : 'text-zinc-900 dark:text-white'
                            }`}
                          >
                            {parts.text}
                          </div>
                          <div className="mt-0.5 text-xs tabular-nums text-zinc-400 dark:text-zinc-400">
                            {formatMoney(line.running_balance)}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          ) : null}
        </>
      )}
    </SidebarLayout>
  );
};

export default StatementDetailPage;
